import React, { useEffect, useState } from "react";
import {
  Flex,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Input,
  Button,
  Box,
  HStack,
  Td,
  Select,
  VStack,
  Link as ChakraLink,
  Avatar,
  Icon,
  Heading,
  Badge,
  InputGroup,
  InputLeftElement,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import axios from "axios";
import { FaMars, FaVenus, FaPlus, FaSearch, FaUserInjured } from "react-icons/fa";
import { FiChevronLeft, FiChevronRight, FiFilter } from "react-icons/fi";
import { getToken, getRole, canManageData } from "utils/auth";
import config from "config";
import { Link } from "react-router-dom";

function Individuals() {
  const [individuals, setIndividuals] = useState([]);
  const [filteredIndividuals, setFilteredIndividuals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCategory, setSearchCategory] = useState("all");
  const [sortCategory, setSortCategory] = useState("");
  const [sortOrder, setSortOrder] = useState("ascendant");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);

  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const tableHeaderBg = useColorModeValue("slate.50", "navy.800");

  useEffect(() => {
    const fetchIndividuals = async () => {
      try {
        const token = getToken();
        const response = await axios.get(
          `${config.API_URL}/individuals?status=Patient`, {
          headers: { Authorization: `Bearer ${token}` }
        }
        );
        setIndividuals(response.data);
        setFilteredIndividuals(response.data);
        setTotalPages(Math.ceil(response.data.length / itemsPerPage));
      } catch (error) {
        console.error("Error fetching individuals registry:", error);
      }
    };

    fetchIndividuals();
  }, [itemsPerPage]);

  const handleSearch = (term, cat) => {
    const currentTerm = term !== undefined ? term : searchTerm;
    const currentCat = cat !== undefined ? cat : searchCategory;
    let filteredData = individuals;

    if (currentTerm) {
      filteredData = filteredData.filter((individual) => {
        if (currentCat === "all") {
          return Object.values(individual)
            .join(" ")
            .toLowerCase()
            .includes(currentTerm.toLowerCase());
        } else {
          return (
            individual[currentCat]
              ?.toString()
              .toLowerCase()
              .includes(currentTerm.toLowerCase())
          );
        }
      });
    }

    setCurrentPage(0);
    setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
    setFilteredIndividuals(filteredData);
  };

  const handleSort = (category) => {
    const newOrder =
      sortCategory === category && sortOrder === "ascendant"
        ? "descendant"
        : "ascendant";
    setSortCategory(category);
    setSortOrder(newOrder);

    const sortedData = [...filteredIndividuals].sort((a, b) => {
      if (newOrder === "ascendant") {
        return a[category] > b[category] ? 1 : -1;
      } else {
        return a[category] < b[category] ? 1 : -1;
      }
    });

    setFilteredIndividuals(sortedData);
  };

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      if (direction === "next" && prev < totalPages - 1) return prev + 1;
      if (direction === "previous" && prev > 0) return prev - 1;
      return prev;
    });
  };

  const displayedData = filteredIndividuals.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <Flex direction="column" w="100%">
      <Box
        p={{ base: "14px", sm: "20px", md: "28px" }}
        bg={cardBg}
        borderRadius="24px"
        border="1px solid"
        borderColor={cardBorder}
        shadow="sm"
        w="100%"
      >
        <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} mb="24px" gap="16px">
          <Box>
            <Heading size="lg" color={textColor} fontWeight="800" letterSpacing="-0.02em">
              Individuals
            </Heading>
            <Text fontSize="sm" color={subtitleColor} mt="4px">
              Catalog of patients and studied individuals
            </Text>
          </Box>
          {canManageData() && (
            <Button
              as={Link}
              to="/txd/individuals/add"
              colorScheme="teal"
              leftIcon={<FaPlus />}
              borderRadius="12px"
              px="20px"
              w={{ base: "100%", md: "auto" }}
            >
              Add New Individual
            </Button>
          )}
        </Flex>

        {/* Filter and Search Bar */}
        <Flex direction={{ base: "column", md: "row" }} gap="12px" mb="24px">
          <InputGroup flex="1">
            <InputLeftElement pointerEvents="none">
              <FaSearch color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by Individual Code, Family, or Region..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                handleSearch(e.target.value, searchCategory);
              }}
              borderRadius="12px"
              bg={useColorModeValue("slate.50", "navy.800")}
            />
          </InputGroup>
          <Select
            value={searchCategory}
            onChange={(e) => {
              setSearchCategory(e.target.value);
              handleSearch(searchTerm, e.target.value);
            }}
            maxW={{ base: "100%", md: "240px" }}
            borderRadius="12px"
            bg={useColorModeValue("slate.50", "navy.800")}
          >
            <option value="all">Search All Fields</option>
            <option value="individual_code">Individual Code</option>
            <option value="family_code">Family Code</option>
            <option value="geographical_origin">Geographical Origin</option>
          </Select>
        </Flex>

        {/* Data Table */}
        <Box overflowX="auto" borderRadius="16px" border="1px solid" borderColor={cardBorder}>
          <Table variant="simple" color={textColor}>
            <Thead bg={tableHeaderBg}>
              <Tr>
                {[
                  { label: "Individual Code", key: "individual_code" },
                  { label: "Status", key: "status" },
                  { label: "Family", key: "family_code" },
                  { label: "Sex", key: "sex" },
                  { label: "Geographical Origin", key: "geographical_origin" },
                ].map(({ label, key }) => (
                  <Th
                    key={key}
                    onClick={() => handleSort(key)}
                    cursor="pointer"
                    py="16px"
                    fontSize="xs"
                    fontWeight="700"
                    color={useColorModeValue("slate.600", "slate.300")}
                    letterSpacing="0.05em"
                    textTransform="uppercase"
                  >
                    <Flex align="center" gap="4px">
                      {label}
                      {sortCategory === key && (
                        <Text as="span" color="teal.400">
                          {sortOrder === "ascendant" ? "▲" : "▼"}
                        </Text>
                      )}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {displayedData.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py="32px" color={subtitleColor}>
                    No individual records found matching your search.
                  </Td>
                </Tr>
              ) : (
                displayedData.map((row, idx) => (
                  <Tr
                    key={idx}
                    _hover={{ bg: useColorModeValue("slate.50", "whiteAlpha.05") }}
                    transition="background 0.15s ease"
                  >
                    <Td py="14px">
                      <ChakraLink
                        as={Link}
                        to={`/txd/individuals/info/${row.individual_code}`}
                        _hover={{ textDecoration: "none" }}
                      >
                        <Flex align="center" gap="12px">
                          <Avatar
                            name={row.individual_code}
                            size="sm"
                            bg={useColorModeValue("brand.100", "teal.900")}
                            color={useColorModeValue("brand.700", "teal.200")}
                            fontWeight="bold"
                            borderRadius="10px"
                          />
                          <Text fontSize="sm" fontWeight="700" color={useColorModeValue("brand.600", "teal.300")}>
                            {row.individual_code}
                          </Text>
                        </Flex>
                      </ChakraLink>
                    </Td>
                    <Td py="14px">
                      <Badge
                        colorScheme={
                          row.status === "Patient" ? "red" : row.status === "Carrier" ? "orange" : "teal"
                        }
                        borderRadius="full"
                        px="10px"
                        py="2px"
                        fontSize="xs"
                        fontWeight="700"
                      >
                        {row.status || "Unknown"}
                      </Badge>
                    </Td>
                    <Td py="14px">
                      <Tag size="sm" colorScheme="cyan" borderRadius="full">
                        <TagLabel fontWeight="600">{row.family_code || "—"}</TagLabel>
                      </Tag>
                    </Td>
                    <Td py="14px">
                      {row.sex === "M" ? (
                        <Tag size="sm" colorScheme="blue" borderRadius="full">
                          <Icon as={FaMars} mr="4px" /> Male
                        </Tag>
                      ) : (
                        <Tag size="sm" colorScheme="pink" borderRadius="full">
                          <Icon as={FaVenus} mr="4px" /> Female
                        </Tag>
                      )}
                    </Td>
                    <Td py="14px" fontSize="sm" color={subtitleColor}>
                      {row.geographical_origin?.replaceAll("|", " & ") || "—"}
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination Section */}
        {totalPages > 1 && (
          <Flex justify="space-between" align="center" mt="20px" wrap="wrap" gap="12px">
            <Text fontSize="xs" color={subtitleColor}>
              Showing page <strong>{currentPage + 1}</strong> of <strong>{totalPages}</strong> ({filteredIndividuals.length} items)
            </Text>
            <HStack spacing="8px">
              <Button
                size="sm"
                leftIcon={<FiChevronLeft />}
                onClick={() => handlePageChange("previous")}
                isDisabled={currentPage === 0}
                borderRadius="10px"
              >
                Previous
              </Button>
              <Button
                size="sm"
                rightIcon={<FiChevronRight />}
                onClick={() => handlePageChange("next")}
                isDisabled={currentPage >= totalPages - 1}
                borderRadius="10px"
              >
                Next
              </Button>
            </HStack>
          </Flex>
        )}
      </Box>
    </Flex>
  );
}

export default Individuals;

